<template>
  <Nav />
  <div class="container">
    <div class="user-container">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1>Usuários</h1>
        <button class="btn btn-secondary" @click="showAddUserModal">
          Novo Usuário
        </button>
      </div>
      <div class="user-filters d-flex gap-2 mb-3 flex-nowrap align-items-center">
        <input
          type="text"
          v-model="filterText"
          placeholder="Buscar por nome ou email..."
          class="form-control user-filter-input"
          @keyup.enter="search"
        />
        <select v-model="filterRole" class="form-select user-filter-select">
          <option value="">Todos os tipos</option>
          <option value="admin">Administrador</option>
          <option value="user">Usuário</option>
        </select>
        <select v-model="filterStatus" class="form-select user-filter-select">
          <option value="">Todos os status</option>
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
        </select>
        <button class="btn btn-secondary" @click="search" :disabled="loading">Buscar</button>
        <button class="btn btn-outline-secondary" @click="clearFilters">Limpar</button>
      </div>
      <div class="user-body">
        <div class="table-responsive">
          <table class="table users-table">
          <thead>
            <tr>
              <th scope="col" class="nome-col">Nome</th>
              <th scope="col" class="email-col">Email</th>
              <th scope="col" class="role-col">Tipo</th>
              <th scope="col" class="actions-col">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.email">
              <td class="nome-col">{{ user.nome }}</td>
              <td class="email-col">{{ user.email }}</td>
              <td class="role-col">
                <span class="badge" :class="user.role === 'admin' ? 'bg-success' : 'bg-secondary'">
                  {{ user.role === 'admin' ? 'Administrador' : 'Usuário' }}
                </span>
              </td>
              <td class="actions-col">
                <div class="d-flex align-items-center justify-content-end">
                  <button class="btn-icon me-2" @click="showEditUserModal(user)">
                    <i class="bi bi-pencil text-primary"></i>
                  </button>
                  <button class="btn-icon me-2" @click="showMunicipiosModalForUser(user)">
                    <i class="bi bi-gear text-secondary"></i>
                  </button>
                  <button class="btn-icon" @click="showDeleteUserModal(user)">
                    <i class="bi bi-trash text-danger"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="users.length === 0">
              <td colspan="4" class="text-center text-muted py-3">Nenhum usuário encontrado.</td>
            </tr>
          </tbody>
        </table>
        </div>

        <!-- Paginação -->
        <div class="fila-pagination d-flex justify-content-between align-items-center mt-3" v-if="total > 0">
          <span class="text-muted small">
            Exibindo {{ users.length }} de {{ total }} registros
          </span>
          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="page === 1"
              @click="goToPage(page - 1)"
            >Anterior</button>
            <span class="btn btn-sm disabled">{{ page }}</span>
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="page * 20 >= total"
              @click="goToPage(page + 1)"
            >Próxima</button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Add/Edit User Modal -->
  <div class="modal fade" id="userModal" tabindex="-1" ref="userModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditing ? 'Editar Usuário' : 'Adicionar Usuário' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveUser">
            <div :class="['mb-3', { 'has-error': (!userForm.nome || userForm.nome.trim() === '') && showValidationErrors }]">
              <label for="nome" class="form-label">Nome</label>
              <input type="text" class="form-control" id="nome" v-model="userForm.nome" required>
            </div>
            <div :class="['mb-3', { 'has-error': (!userForm.email || userForm.email.trim() === '') && showValidationErrors }]">
              <label for="email" class="form-label">Email</label>
              <input type="email" class="form-control" id="email" v-model="userForm.email" required :disabled="isEditing">
            </div>
            <div :class="['mb-3', { 'has-error': (!isEditing && (!userForm.password || userForm.password.trim() === '')) && showValidationErrors }]">
              <label for="password" class="form-label">Senha</label>
              <div class="input-group">
                <input type="text" class="form-control" id="password" v-model="userForm.password" :required="!isEditing">
                <button type="button" class="btn btn-outline-secondary" @click="generatePassword">
                  <i class="bi bi-arrow-clockwise"></i>
                </button>
              </div>
              <div class="form-text">{{ isEditing ? 'Deixe em branco para manter a senha atual' : 'Clique no botão para gerar uma senha aleatória' }}</div>
            </div>
            <div :class="['mb-3', { 'has-error': (!userForm.role || userForm.role.trim() === '') && showValidationErrors }]">
              <label for="role" class="form-label">Tipo de usuário</label>
              <select class="form-select" id="role" v-model="userForm.role" required>
                <option value="">Selecione...</option>
                <option value="user">Normal</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <div class="mb-3 form-check" v-if="isEditing">
              <input type="checkbox" class="form-check-input" id="inativo" v-model="userForm.inativo">
              <label class="form-check-label" for="inativo">Usuário inativo</label>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-secondary" :disabled="!isFormValid" @click="saveUser">{{ isEditing ? 'Atualizar' : 'Criar' }}</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete User Modal -->
  <div class="modal fade" id="deleteModal" tabindex="-1" ref="deleteModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Confirmar Exclusão</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <p>Tem certeza que deseja excluir o usuário <strong>{{ userToDelete?.nome }}</strong> ({{ userToDelete?.email }})?</p>
          <p class="text-danger">Esta ação não pode ser desfeita.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-danger" @click="deleteUser">Excluir</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Municipios Linking Modal -->
  <div class="modal fade" id="municipiosModal" tabindex="-1" ref="municipiosModal">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title">Gerenciar Municípios</h5>
            <div class="text-muted small">{{ currentUserName }} - {{ currentUserEmail }}</div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body">
          <div class="row">
            <!-- Available Municipios Panel -->
            <div class="col-md-6">
              <div class="panel-header d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">Municípios Disponíveis</h6>
                <button type="button" class="btn btn-sm btn-secondary" @click="moveAllToLinked" :disabled="filteredAvailableMunicipios.length === 0">
                  Adicionar Todos <i class="bi bi-arrow-right"></i>
                </button>
              </div>
              <div class="filter-container mb-3">
                <input
                  type="text"
                  v-model="availableFilterText"
                  @keyup.enter="applyAvailableFilter"
                  placeholder="Buscar municípios..."
                  class="form-control"
                />
                <button class="btn btn-outline-secondary btn-sm ms-2" @click="clearAvailableFilter">Limpar</button>
              </div>
              <div class="municipios-list">
                <ul class="list-group municipios-scroll" v-if="filteredAvailableMunicipios.length > 0">
                  <li
                    v-for="municipio in filteredAvailableMunicipios"
                    :key="municipio.codigo_siconfi"
                    class="list-group-item municipio-item"
                    @click="moveToLinked(municipio)"
                  >
                    {{ municipio.uf }} - {{ municipio.razao_social.replace("MUNICÍPIO DE ", "") }}{{ municipio.demo ? " (DEMO)" : "" }}
                    <i class="bi bi-plus-circle text-primary float-end"></i>
                  </li>
                </ul>
                <p v-else class="text-center text-muted">Nenhum município disponível.</p>
              </div>
            </div>

            <!-- Linked Municipios Panel -->
            <div class="col-md-6">
              <div class="panel-header d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">Municípios Vinculados</h6>
                <button type="button" class="btn btn-sm btn-danger" @click="moveAllToAvailable" :disabled="filteredLinkedMunicipios.length === 0">
                  <i class="bi bi-arrow-left"></i> Remover Todos
                </button>
              </div>
              <div class="filter-container mb-3">
                <input
                  type="text"
                  v-model="linkedFilterText"
                  @keyup.enter="applyLinkedFilter"
                  placeholder="Buscar municípios..."
                  class="form-control"
                />
                <button class="btn btn-outline-secondary btn-sm ms-2" @click="clearLinkedFilter">Limpar</button>
              </div>
              <div class="municipios-list">
                <ul class="list-group municipios-scroll" v-if="filteredLinkedMunicipios.length > 0">
                  <li
                    v-for="municipio in filteredLinkedMunicipios"
                    :key="municipio.codigo_siconfi"
                    class="list-group-item municipio-item"
                    @click="moveToAvailable(municipio)"
                  >
                    {{ municipio.uf }} - {{ municipio.razao_social.replace("MUNICÍPIO DE ", "") }}{{ municipio.demo ? " (DEMO)" : "" }}
                    <i class="bi bi-dash-circle text-danger float-end"></i>
                  </li>
                </ul>
                <p v-else class="text-center text-muted">Nenhum município vinculado.</p>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
          <button type="button" class="btn btn-secondary" @click="saveMunicipiosAssociation">Salvar Alterações</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosApi from "@/axios";
import Nav from "@/components/Nav.vue";
import { mapState, mapActions } from "vuex";
// Modal is available via window.bootstrap from CDN

export default {
  name: "Users",
  data() {
    return {
      users: [],
      isEditing: false,
      userForm: {
        nome: '',
        email: '',
        password: '',
        role: 'user',
        inativo: false
      },
      userToDelete: null,
      // Municipios management
      currentUserEmail: '',
      currentUserName: '',
      allMunicipios: [],
      availableMunicipios: [],
      linkedMunicipios: [],
      availableFilterText: '',
      linkedFilterText: '',
      appliedAvailableFilterText: '',
      appliedLinkedFilterText: '',
      isNewUserFlow: false,
      showValidationErrors: false,
      // filter state
      filterText: '',
      filterRole: '',
      filterStatus: '',
      // pagination state
      page: 1,
      total: 0,
      totalPages: 0,
      loading: false,
    };
  },
  computed: {
    ...mapState(["user", "isAuthLoading"]),
    isFormValid() {
      const isNameValid = this.userForm.nome && this.userForm.nome.trim() !== '';
      const isEmailValid = this.userForm.email && this.userForm.email.trim() !== '';
      const isPasswordValid = this.isEditing || (this.userForm.password && this.userForm.password.trim() !== '');
      const isRoleValid = this.userForm.role && this.userForm.role.trim() !== '';
      
      return isNameValid && isEmailValid && isPasswordValid && isRoleValid;
    },
    filteredAvailableMunicipios() {
      if (!this.availableMunicipios || !Array.isArray(this.availableMunicipios)) {
        return [];
      }
      return this.availableMunicipios.filter((municipio) => {
        if (!municipio || !municipio.uf || !municipio.razao_social) {
          return false;
        }
        const searchText = this.appliedAvailableFilterText.toLowerCase();
        const textToFilter = `${municipio.uf} - ${municipio.razao_social.replace("MUNICÍPIO DE ", "")}${municipio.demo ? " (DEMO)" : ""}`;
        return textToFilter.toLowerCase().includes(searchText);
      });
    },
    filteredLinkedMunicipios() {
      if (!this.linkedMunicipios || !Array.isArray(this.linkedMunicipios)) {
        return [];
      }
      return this.linkedMunicipios.filter((municipio) => {
        if (!municipio || !municipio.uf || !municipio.razao_social) {
          return false;
        }
        const searchText = this.appliedLinkedFilterText.toLowerCase();
        const textToFilter = `${municipio.uf} - ${municipio.razao_social.replace("MUNICÍPIO DE ", "")}${municipio.demo ? " (DEMO)" : ""}`;
        return textToFilter.toLowerCase().includes(searchText);
      });
    },
  },
  async created() {
    try {
      await this.validateToken();
      this.checkUserEntidade();
      
      // Wait for auth loading to complete before checking user role
      if (this.isAuthLoading) {
        const checkAuth = () => {
          if (!this.isAuthLoading) {
            if (!this.user || this.user.role !== 'admin') {
              this.$router.push("/");
            } else {
              this.getUsers();
            }
          } else {
            setTimeout(checkAuth, 100);
          }
        };
        checkAuth();
      } else {
        if (!this.user || this.user.role !== 'admin') {
          this.$router.push("/");
        } else {
          this.getUsers();
        }
      }
    } catch (error) {
      console.log(error);
      
      // Handle deleted user specifically
      if (error.message === "USER_DELETED") {
        this.showToast({
          title: 'Sessão Encerrada',
          message: 'Sua conta foi removida do sistema. Entre em contato com o administrador.',
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
      
      this.$router.push("/login");
    }
  },
  watch: {
    filterRole() {
      if (this._suppressWatch) return;
      this.page = 1;
      this.getUsers();
    },
    filterStatus() {
      if (this._suppressWatch) return;
      this.page = 1;
      this.getUsers();
    },
  },
  methods: {
    ...mapActions(["validateToken", "showToast"]),
    async getUsers() {
      this.loading = true;
      try {
        const params = new URLSearchParams({
          page: this.page,
          limit: 20,
          ...(this.filterText.trim() && { search: this.filterText.trim() }),
          ...(this.filterRole && { role: this.filterRole }),
          ...(this.filterStatus && { status: this.filterStatus }),
        });
        const response = await axiosApi.get(`/api/users?${params}`);
        this.users = response.data.data;
        this.total = response.data.pagination.total;
        this.totalPages = response.data.pagination.totalPages;
        this.page = response.data.pagination.page;
      } catch (error) {
        console.error(error);
        this.showToast({
          title: 'Erro',
          message: 'Não foi possível carregar os usuários.',
          type: 'toast-danger',
          headerClass: 'toast-header-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    goToPage(p) {
      if (p < 1 || p > this.totalPages || p === this.page) return;
      this.page = p;
      this.getUsers();
    },
    checkUserEntidade() {
      if (!this.$store.state.userEntidade) {
        this.$router.push("/selecaoEntidades");
      }
    },
    showAddUserModal() {
      this.isEditing = false;
      this.resetForm();
      this.showValidationErrors = false;
      this.showModal('userModal');
    },
    showEditUserModal(user) {
      this.isEditing = true;
      this.userForm = {
        nome: user.nome || '',
        email: user.email,
        password: '',
        role: user.role || 'user',
        inativo: user.inativo || false
      };
      this.showValidationErrors = false;
      this.showModal('userModal');
    },
    showDeleteUserModal(user) {
      this.userToDelete = user;
      this.showModal('deleteModal');
    },
    showModal(modalId) {
      const modal = new window.bootstrap.Modal(document.getElementById(modalId));
      modal.show();
      
      // Add event listener for municipios modal to reset filters when closed
      if (modalId === 'municipiosModal') {
        const modalElement = document.getElementById(modalId);
        modalElement.addEventListener('hidden.bs.modal', () => {
          this.resetMunicipiosFilters();
        }, { once: true }); // Use once: true to prevent multiple listeners
      }
    },
    hideModal(modalId) {
      const modal = window.bootstrap.Modal.getInstance(document.getElementById(modalId));
      if (modal) {
        modal.hide();
      }
    },
    resetForm() {
      this.userForm = {
        nome: '',
        email: '',
        password: '',
        role: 'user',
        inativo: false
      };
    },
    generatePassword() {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
      let password = '';
      for (let i = 0; i < 8; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      this.userForm.password = password;
    },
    async saveUser() {
      try {
        // Check if form is valid
        if (!this.isFormValid) {
          this.showValidationErrors = true;
          return;
        }
        
        this.showValidationErrors = false;

        const userData = {
          nome: this.userForm.nome,
          role: this.userForm.role,
          inativo: this.userForm.inativo
        };

        // Only include entidades for new users, not when editing
        if (!this.isEditing) {
          userData.entidades = []; // Empty for new users, municipios modal will handle associations
        }

        // Only include password if it's provided (for new users it's required, for editing it's optional)
        if (this.userForm.password && this.userForm.password.trim() !== '') {
          userData.password = this.userForm.password;
        }

        let response;
        if (this.isEditing) {
          response = await axiosApi.put(`/api/users/${this.userForm.email}`, userData);
        } else {
          userData.email = this.userForm.email;
          response = await axiosApi.post('/api/users', userData);
        }

        if (response.data.success) {
          const userEmail = this.userForm.email; // Store email before resetForm
          const userName = this.userForm.nome; // Store name before resetForm
          const wasEditing = this.isEditing;
          
          this.hideModal('userModal'); // Always close user modal
          this.getUsers(); // Refresh the user list
          this.showToast({
            title: 'Sucesso',
            message: wasEditing ? `Usuário ${userEmail} atualizado com sucesso!` : `Usuário ${userEmail} criado com sucesso!`,
            type: 'toast-success',
            headerClass: 'toast-header-success'
          });
          this.resetForm();
          
          // Show municipios modal for new users
          if (!wasEditing) {
            setTimeout(() => {
              this.showMunicipiosModalForNewUser(userEmail, userName);
            }, 500); // Small delay to ensure modal transition
          }
        } else {
          this.showToast({
            title: 'Erro',
            message: response.data.message,
            type: 'toast-danger',
            headerClass: 'toast-header-danger'
          });
        }
      } catch (error) {
        console.error('Error saving user:', error);
        this.showToast({
          title: 'Erro',
          message: `Erro ao salvar usuário ${this.userForm.email}: ` + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    async deleteUser() {
      try {
        const response = await axiosApi.delete(`/api/users/${this.userToDelete.email}`);
        
        if (response.data.success) {
          const userEmail = this.userToDelete.email; // Store email before setting to null
          this.hideModal('deleteModal');
          this.getUsers(); // Refresh the user list
          this.showToast({
            title: 'Sucesso',
            message: `Usuário ${userEmail} excluído com sucesso!`,
            type: 'toast-success',
            headerClass: 'toast-header-success'
          });
          this.userToDelete = null;
        } else {
          this.showToast({
            title: 'Erro',
            message: response.data.message,
            type: 'toast-danger',
            headerClass: 'toast-header-danger'
          });
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        this.showToast({
          title: 'Erro',
          message: `Erro ao excluir usuário ${this.userToDelete?.email}: ` + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    // Municipios management methods
    async showMunicipiosModalForUser(user) {
      this.currentUserEmail = user.email;
      this.currentUserName = user.nome;
      this.isNewUserFlow = false; // This is for editing existing users
      await this.loadAllMunicipios();
      await this.loadUserMunicipios();
      this.showModal('municipiosModal');
    },
    async showMunicipiosModal() {
      this.currentUserEmail = this.userForm.email;
      this.currentUserName = this.userForm.nome;
      this.isNewUserFlow = false; // This is for editing existing users
      await this.loadAllMunicipios();
      await this.loadUserMunicipios();
      this.showModal('municipiosModal');
    },
    async showMunicipiosModalForNewUser(userEmail, userName) {
      this.currentUserEmail = userEmail;
      this.currentUserName = userName;
      this.isNewUserFlow = true; // Flag to track this is for a new user
      await this.loadAllMunicipios();
      this.linkedMunicipios = []; // New users have no linked municipios
      this.availableMunicipios = [...this.allMunicipios];
      this.showModal('municipiosModal');
    },
    async loadAllMunicipios() {
      try {
        // Load all municipios from the system (unpaginated - needed for full linking list)
        const response = await axiosApi.get('/api/entidades?all=true');
        this.allMunicipios = Array.isArray(response.data.data) ? response.data.data.sort((a, b) => {
          if (a.uf === b.uf) {
            return a.cidade.localeCompare(b.cidade);
          }
          return a.uf.localeCompare(b.uf);
        }) : [];
      } catch (error) {
        console.error('Error loading municipios:', error);
        this.allMunicipios = [];
        this.showToast({
          title: 'Erro',
          message: 'Erro ao carregar municípios: ' + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    async loadUserMunicipios() {
      try {
        console.log('Loading municipios for user:', this.currentUserEmail);
        
        // Encode the email to handle special characters
        const encodedEmail = encodeURIComponent(this.currentUserEmail);
        
        // Load linked municipios for the current user
        const response = await axiosApi.get(`/api/users/${encodedEmail}/entidades`);
        console.log('User municipios response:', response.data);
        
        this.linkedMunicipios = Array.isArray(response.data.data) ? response.data.data.sort((a, b) => {
          if (a.uf === b.uf) {
            return a.cidade.localeCompare(b.cidade);
          }
          return a.uf.localeCompare(b.uf);
        }) : [];
        
        // Calculate available municipios (all minus linked)
        this.availableMunicipios = Array.isArray(this.allMunicipios) ? this.allMunicipios.filter(municipio => 
          !this.linkedMunicipios.some(linked => linked.codigo_siconfi === municipio.codigo_siconfi)
        ) : [];
        
        console.log('Linked municipios:', this.linkedMunicipios.length);
        console.log('Available municipios:', this.availableMunicipios.length);
      } catch (error) {
        console.error('Error loading user municipios:', error);
        console.error('Full error details:', error.response);
        this.linkedMunicipios = [];
        this.availableMunicipios = [];
        this.showToast({
          title: 'Erro',
          message: 'Erro ao carregar municípios do usuário: ' + (error.response?.data?.message || error.message),
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
    moveToLinked(municipio) {
      if (!municipio || !municipio.codigo_siconfi) return;
      // Move from available to linked
      this.availableMunicipios = Array.isArray(this.availableMunicipios) ? 
        this.availableMunicipios.filter(m => m.codigo_siconfi !== municipio.codigo_siconfi) : [];
      if (!Array.isArray(this.linkedMunicipios)) {
        this.linkedMunicipios = [];
      }
      this.linkedMunicipios.push(municipio);
      this.sortMunicipios();
    },
    moveToAvailable(municipio) {
      if (!municipio || !municipio.codigo_siconfi) return;
      // Move from linked to available
      this.linkedMunicipios = Array.isArray(this.linkedMunicipios) ? 
        this.linkedMunicipios.filter(m => m.codigo_siconfi !== municipio.codigo_siconfi) : [];
      if (!Array.isArray(this.availableMunicipios)) {
        this.availableMunicipios = [];
      }
      this.availableMunicipios.push(municipio);
      this.sortMunicipios();
    },
    moveAllToLinked() {
      // Move all filtered available municipios to linked
      const toMove = [...this.filteredAvailableMunicipios];
      toMove.forEach(municipio => {
        this.availableMunicipios = this.availableMunicipios.filter(m => m.codigo_siconfi !== municipio.codigo_siconfi);
        this.linkedMunicipios.push(municipio);
      });
      this.sortMunicipios();
      this.clearAvailableFilter();
    },
    moveAllToAvailable() {
      // Move all filtered linked municipios to available
      const toMove = [...this.filteredLinkedMunicipios];
      toMove.forEach(municipio => {
        this.linkedMunicipios = this.linkedMunicipios.filter(m => m.codigo_siconfi !== municipio.codigo_siconfi);
        this.availableMunicipios.push(municipio);
      });
      this.sortMunicipios();
      this.clearLinkedFilter();
    },
    sortMunicipios() {
      // Sort both arrays by UF and then by city name
      if (Array.isArray(this.availableMunicipios)) {
        this.availableMunicipios.sort((a, b) => {
          if (a.uf === b.uf) {
            return a.cidade.localeCompare(b.cidade);
          }
          return a.uf.localeCompare(b.uf);
        });
      }
      
      if (Array.isArray(this.linkedMunicipios)) {
        this.linkedMunicipios.sort((a, b) => {
          if (a.uf === b.uf) {
            return a.cidade.localeCompare(b.cidade);
          }
          return a.uf.localeCompare(b.uf);
        });
      }
    },
    applyAvailableFilter() {
      this.appliedAvailableFilterText = this.availableFilterText;
    },
    applyLinkedFilter() {
      this.appliedLinkedFilterText = this.linkedFilterText;
    },
    clearAvailableFilter() {
      this.availableFilterText = '';
      this.appliedAvailableFilterText = '';
    },
    clearLinkedFilter() {
      this.linkedFilterText = '';
      this.appliedLinkedFilterText = '';
    },
    resetMunicipiosFilters() {
      this.availableFilterText = '';
      this.linkedFilterText = '';
      this.appliedAvailableFilterText = '';
      this.appliedLinkedFilterText = '';
    },
    search() {
      this.page = 1;
      this.getUsers();
    },
    clearFilters() {
      this._suppressWatch = true;
      this.filterText = '';
      this.filterRole = '';
      this.filterStatus = '';
      this.page = 1;
      this.$nextTick(() => {
        this._suppressWatch = false;
      });
      this.getUsers();
    },
    async saveMunicipiosAssociation() {
      try {
        console.log('Saving municipios for user:', this.currentUserEmail);
        
        // Encode the email to handle special characters
        const encodedEmail = encodeURIComponent(this.currentUserEmail);
        
        // Get array of linked municipio codes
        const entidadeCodes = this.linkedMunicipios.map(m => m.codigo_siconfi);
        console.log('Municipio codes to associate:', entidadeCodes);
        
        // First, dissociate all current municipios (clean slate approach)
        try {
          const currentMunicipiosResponse = await axiosApi.get(`/api/users/${encodedEmail}/entidades`);
          const currentCodes = currentMunicipiosResponse.data.data.map(m => m.codigo_siconfi);
          
          if (currentCodes.length > 0) {
            console.log('Dissociating current municipios:', currentCodes);
            await axiosApi.post(`/api/users/${encodedEmail}/dissociate-entidade`, {
              entidades: currentCodes
            });
          }
        } catch (dissocError) {
          console.log('No current municipios to dissociate or error:', dissocError);
        }
        
        // Then associate the new municipios
        if (entidadeCodes.length > 0) {
          console.log('Associating new municipios:', entidadeCodes);
          const response = await axiosApi.post(`/api/users/${encodedEmail}/associate-entidade`, {
            entidades: entidadeCodes
          });
          
          console.log('Association response:', response.data);
          
          if (!response.data.success) {
            throw new Error(response.data.message || 'Erro ao associar municípios');
          }
        }

        // Close modals and show success message
        this.hideModal('municipiosModal');
        this.resetMunicipiosFilters(); // Reset search fields
        this.isNewUserFlow = false; // Reset the flag
        
        this.showToast({
          title: 'Sucesso',
          message: `Municípios do usuário ${this.currentUserEmail} atualizados com sucesso!`,
          type: 'toast-success',
          headerClass: 'toast-header-success'
        });
        
      } catch (error) {
        console.error('Error saving municipios association:', error);
        console.error('Full error details:', error.response);
        
        let errorMessage = 'Erro ao salvar associações';
        if (error.response?.status === 404) {
          errorMessage = `Usuário ${this.currentUserEmail} não encontrado`;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.showToast({
          title: 'Erro',
          message: errorMessage,
          type: 'toast-danger',
          headerClass: 'toast-header-danger'
        });
      }
    },
  },
  components: {
    Nav,
  },
};
</script>

<style>
.table {
  margin-top: 20px;
}

.user-container {
  min-width: 1200px;
}

@media (max-width: 1260px) {
  .user-container {
    min-width: 0;
    width: 100%;
  }
}

.btn-icon {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1em;
}

.btn-icon:hover {
  opacity: 0.7;
}

/* Municipios Modal Styles */
.municipios-scroll {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 0.375rem;
}

.municipio-item {
  cursor: pointer;
  transition: background-color 0.2s;
  border: none !important;
  border-bottom: 1px solid #eee !important;
}

.municipio-item:hover {
  background-color: var(--azul-claro, #e3f2fd);
  color: var(--azul-final, #1976d2);
  font-weight: 500;
}

.municipio-item:last-child {
  border-bottom: none !important;
}

.panel-header h6 {
  color: var(--azul-final, #1976d2);
  font-weight: 600;
}

.filter-container {
  display: flex;
  gap: 5px;
}

.municipios-list {
  height: 450px;
  display: flex;
  flex-direction: column;
}

.municipios-list p {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Modal adjustments */
.modal-xl .modal-body {
  padding: 1.5rem;
}

.modal-xl .modal-dialog {
  max-width: 1200px;
}

.btn-secondary .bi-arrow-right {
  color: white;
  font-size: 1.2em;
  line-height: 1;
  vertical-align: middle;
  display: inline-block;
}

.btn-danger .bi-arrow-left {
  color: white;
  font-size: 1.2em;
  line-height: 1;
  vertical-align: middle;
  display: inline-block;
}

/* Users table fixed column widths */
.users-table {
  table-layout: fixed;
  width: 100%;
}

.users-table .nome-col {
  width: 25%;
}

.users-table .email-col {
  width: 30%;
}

.users-table .role-col {
  width: 20%;
  text-align: center;
}

.users-table .actions-col {
  width: 25%;
  text-align: right;
}

/* Ensure table cells maintain consistent widths */
.users-table td.nome-col {
  width: 25%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.users-table td.email-col {
  width: 30%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.users-table td.role-col {
  width: 20%;
  text-align: center;
}

.users-table td.actions-col {
  width: 25%;
  text-align: right;
}

/* Form validation styling */
.has-error .form-control,
.has-error .form-select {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.has-error .form-label {
  color: #dc3545;
}

.user-filter-input {
  min-width: 200px;
}

.user-filter-select {
  max-width: 180px;
  min-width: 150px;
}
</style>
